import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setAuth, isAuthed } from "@/lib/auth";
import { toast } from "sonner";
import { ITMS } from "@/constants/testIds";
import { Bus, ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (isAuthed()) navigate("/dashboard", { replace: true }); }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuth(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2">
      {/* Left brand */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0F1A3D] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white text-[#1E3A8A] flex items-center justify-center font-display font-black text-xl">K</div>
            <div>
              <div className="font-display font-bold text-sm">KTCL - MajheApp Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Integrated Transport Management</div>
            </div>
          </div>
        </div>
        <div className="relative space-y-6">
          <div className="label-cap text-slate-400">Operations Control Center</div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            One screen. Every bus. <br/>
            <span className="text-[#22C55E]">Live across Goa.</span>
          </h1>
          <p className="text-slate-300 max-w-md leading-relaxed text-sm">
            Real-time fleet tracking, drag-and-drop schedule planning, dispatch operations,
            private operator settlements and enterprise reporting for Kadamba Transport Corporation Limited.
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md pt-4">
            {[
              ["GPS Connectivity", "98.4%"],
              ["Active Fleet", "612"],
              ["Routes Live", "143"],
              ["Daily Passengers", "82K+"],
            ].map(([k, v]) => (
              <div key={k} className="border border-white/10 rounded-md p-3">
                <div className="text-xs text-slate-400">{k}</div>
                <div className="font-display font-bold text-lg">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 Kadamba Transport Corporation Limited · Govt. of Goa</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center font-display font-black">K</div>
            <div className="font-display font-bold">KTCL - MajheApp Admin</div>
          </div>
          <div>
            <div className="label-cap text-slate-500 mb-2">Secure Sign-In</div>
            <h2 className="font-display text-3xl font-bold">Welcome back.</h2>
            <p className="text-sm text-slate-500 mt-1">Sign in to the KTCL operations control center.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600">Work Email</label>
              <input
                data-testid={ITMS.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] focus:bg-white transition-colors"
                placeholder="admin@ktcl.gov.in"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Password</label>
              <div className="relative mt-1">
                <input
                  data-testid={ITMS.loginPassword}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 pr-10 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A] focus:bg-white transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeSlash size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>
          </div>

          <button
            data-testid={ITMS.loginSubmit}
            disabled={loading}
            className="w-full ktcl-btn-primary rounded-md py-2.5 text-sm font-semibold disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Sign in to ITMS"}
          </button>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-[#22C55E]" />
            JWT + RBAC secured · MFA available
          </div>
        </form>
      </div>
    </div>
  );
}
